import axios from 'axios';

const stripTrailingSlash = (value) => String(value || '').replace(/\/+$/, '');

const getDefaultApiBaseUrl = () => {
  if (typeof window === 'undefined') {
    return '/api';
  }

  const { protocol, hostname, pathname } = window.location;
  const originHost = `${protocol}//${hostname}`;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${originHost}/HOST/backend/api`;
  }

  const normalizedPath = pathname.replace(/\/+$/, '') || '/';

  if (/\/HOST(?:\/|$)/i.test(normalizedPath)) {
    const basePath = normalizedPath.split('/HOST')[0] || '';
    return `${originHost}${basePath}/HOST/backend/api`;
  }

  if (/\/backend(?:\/|$)/i.test(normalizedPath)) {
    const basePath = normalizedPath.split('/backend')[0] || '';
    return `${originHost}${basePath}/backend/api`;
  }

  return `${originHost}/api`;
};

export const API_BASE_URL = stripTrailingSlash(
  process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || process.env.REACT_APP_API_ROOT_URL || getDefaultApiBaseUrl()
);

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // If sending FormData, allow the browser to set the multipart boundary.
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const getApiRootUrl = () => {
  const configuredRoot = process.env.REACT_APP_API_ROOT_URL || process.env.REACT_APP_API_URL;
  if (configuredRoot) {
    return stripTrailingSlash(configuredRoot);
  }

  const baseUrl = stripTrailingSlash(API_BASE_URL);

  if (/\/backend\/api$/i.test(baseUrl)) {
    return baseUrl.replace(/\/api$/i, '');
  }

  if (/\/api$/i.test(baseUrl)) {
    return baseUrl.replace(/\/api$/i, '');
  }

  return baseUrl;
};

export const getAssetUrl = (path) => {
  if (!path) return '';

  const rawPath = String(path);
  if (/^(https?:)?\/\//i.test(rawPath) || rawPath.startsWith('data:') || rawPath.startsWith('blob:')) {
    return rawPath;
  }

  const rootUrl = getApiRootUrl();
  let cleanPath = rawPath.replace(/^\/+/, '');

  if (/\/backend$/i.test(rootUrl) && cleanPath.toLowerCase().startsWith('backend/')) {
    cleanPath = cleanPath.slice('backend/'.length);
  }

  return rootUrl ? `${rootUrl}/${cleanPath}` : `/${cleanPath}`;
};

const getPhotoUploadFilename = (file, fallbackFilename) => {
  if (fallbackFilename) return fallbackFilename;
  if (file?.name) return file.name;

  const extensionByType = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
  };
  const extension = extensionByType[file?.type] || 'png';

  return `student-photo.${extension}`;
};

// Authentication APIs
export const authAPI = {
  login: (credentials) => api.post('/auth.php?action=login', credentials),
  validateToken: () => api.post('/auth.php?action=validate'),
  changePassword: (data) => api.post('/auth.php?action=change-password', data),
};

// Student APIs
export const studentAPI = {
  getAll: (params) => api.get('/students.php', { params }),
  getNextEnrollment: () => api.get('/students.php?next_enrollment=1'),
  getById: (id) => api.get(`/students.php?id=${id}`),
  create: (data) => api.post('/students.php', data),
  update: (id, data) => api.put(`/students.php?id=${id}`, data),
  delete: (id) => api.delete(`/students.php?id=${id}`),
};

// Pending Student Requests APIs (Teacher submissions for admin approval)
export const pendingStudentAPI = {
  getAll: (params) => api.get('/pending-students.php', { params }),
  getById: (id) => api.get(`/pending-students.php?id=${id}`),
  submit: (data) => api.post('/pending-students.php', data),
  approve: (id, data) => api.put(`/pending-students.php?id=${id}`, data),
  reject: (id, data) => api.put(`/pending-students.php?id=${id}`, data),
  delete: (id) => api.delete(`/pending-students.php?id=${id}`),
};

export const photoUploadAPI = {
  upload: (file, filename) => {
    const formData = new FormData();
    formData.append('photo', file, getPhotoUploadFilename(file, filename));

    return api.post('/photo-upload.php', formData);
  },
};

// Teacher APIs
export const teacherAPI = {
  getAll: (params) => api.get('/teachers.php', { params }),
  getById: (id) => api.get(`/teachers.php?id=${id}`),
  create: (data) => api.post('/teachers.php', data),
  update: (id, data) => api.put(`/teachers.php?id=${id}`, data),
  delete: (id) => api.delete(`/teachers.php?id=${id}`),
};

// Class and Fees APIs
export const classFeesAPI = {
  getSettings: () => api.get('/class-fees.php'),
  createClass: (data) => api.post('/class-fees.php?resource=class', data),
  updateClass: (id, data) => api.put(`/class-fees.php?resource=class&id=${id}`, data),
  createFee: (data) => api.post('/class-fees.php?resource=fee', data),
  updateFee: (id, data) => api.put(`/class-fees.php?resource=fee&id=${id}`, data),
  deleteFee: (id) => api.delete(`/class-fees.php?resource=fee&id=${id}`),
  createVehicleFee: (data) => api.post('/class-fees.php?resource=vehicle-fee', data),
  updateVehicleFee: (id, data) => api.put(`/class-fees.php?resource=vehicle-fee&id=${id}`, data),
  deleteVehicleFee: (id) => api.delete(`/class-fees.php?resource=vehicle-fee&id=${id}`),
};

// Fee Payment APIs
export const feePaymentAPI = {
  searchStudents: (search) => api.get('/fee-payments.php', { params: { search } }),
  getStudentFees: (studentId) => api.get('/fee-payments.php', { params: { student_id: studentId } }),
  collectPayment: (data) => api.post('/fee-payments.php', data),
};

// Attendance APIs
export const attendanceAPI = {
  getAttendance: (params) => api.get('/attendance.php', { params }),
  markAttendance: (data) => api.post('/attendance.php', data),
  updateAttendance: (data) => api.put('/attendance.php', data),
  getStats: (params) => api.get('/attendance.php', { params: { ...params, stats: true } }),
};

// Marks APIs
export const marksAPI = {
  getMarks: (params) => api.get('/marks.php', { params }),
  uploadMarks: (data) => api.post('/marks.php', data),
  updateMarks: (data) => api.put('/marks.php', data),
  getReportCard: (studentId, examId) => 
    api.get('/marks.php', { params: { student_id: studentId, exam_id: examId, report_card: true } }),
};

// Exam APIs
export const examAPI = {
  getAll: (params) => api.get('/exams.php', { params }),
  getById: (id) => api.get(`/exams.php?id=${id}`),
  create: (data) => api.post('/exams.php', data),
  update: (id, data) => api.put(`/exams.php?id=${id}`, data),
  delete: (id) => api.delete(`/exams.php?id=${id}`),
};

// Subject APIs
export const subjectAPI = {
  getAll: (params) => api.get('/subjects.php', { params }),
  getByStandard: (standard) => api.get(`/subjects.php?standard=${standard}`),
  create: (data) => api.post('/subjects.php', data),
  update: (id, data) => api.put(`/subjects.php?id=${id}`, data),
  delete: (id) => api.delete(`/subjects.php?id=${id}`),
};

// Dashboard APIs
export const dashboardAPI = {
  getAdminStats: () => api.get('/dashboard.php?role=admin'),
  getTeacherStats: () => api.get('/dashboard.php?role=teacher'),
  getStudentStats: () => api.get('/dashboard.php?role=student'),
};

// Classes/Standards APIs
export const classesAPI = {
  getAll: () => api.get('/classes.php'),
  create: (data) => api.post('/classes.php', data),
  update: (id, data) => api.put(`/classes.php?id=${id}`, data),
  delete: (id) => api.delete(`/classes.php?id=${id}`),
};

// Academic Years APIs
export const academicYearsAPI = {
  getAll: () => api.get('/academic-years.php'),
  create: (data) => api.post('/academic-years.php', data),
  update: (id, data) => api.put(`/academic-years.php?id=${id}`, data),
  delete: (id) => api.delete(`/academic-years.php?id=${id}`),
};

// Class-Teacher Assignments APIs
export const classAssignmentsAPI = {
  getAll: (params) => api.get('/class-assignments.php', { params }),
  getByTeacher: (teacherId) => api.get('/class-assignments.php', { params: { teacher_id: teacherId } }),
  getByClass: (standard, section) => 
    api.get('/class-assignments.php', { params: { standard, section } }),
  create: (data) => api.post('/class-assignments.php', data),
  update: (id, data) => api.put(`/class-assignments.php?id=${id}`, data),
  delete: (id) => api.delete(`/class-assignments.php?id=${id}`),
};

// User Management APIs
export const usersAPI = {
  getAll: (params) => api.get('/users.php', { params }),
  create: (data) => api.post('/users.php', data),
  update: (id, data) => api.put(`/users.php?id=${id}`, data),
  changeRole: (id, role) => api.put(`/users.php?action=change-role&id=${id}`, { role }),
  resetPassword: (userId, newPassword) => 
    api.post('/users.php?action=reset-password', { user_id: userId, new_password: newPassword }),
  delete: (id) => api.delete(`/users.php?id=${id}`),
};

// Hall Tickets APIs
export const hallTicketsAPI = {
  getAll: (params) => api.get('/hall-tickets.php', { params }),
  getByExam: (examId) => api.get('/hall-tickets.php', { params: { exam_id: examId } }),
  getByStudent: (studentId) => api.get('/hall-tickets.php', { params: { student_id: studentId } }),
  create: (data) => api.post('/hall-tickets.php', data),
  generateBatch: (data) => api.post('/hall-tickets.php?action=generate-batch', data),
  batchGenerate: (data) => api.post('/hall-tickets.php?action=generate-batch', data),
  update: (id, data) => api.put(`/hall-tickets.php?id=${id}`, data),
  delete: (id) => api.delete(`/hall-tickets.php?id=${id}`),
};

// Notifications APIs
export const notificationsAPI = {
  getAll: () => api.get('/notifications.php'),
  create: (data) => api.post('/notifications.php', data),
  update: (id, data) => api.put(`/notifications.php?id=${id}`, data),
  delete: (id) => api.delete(`/notifications.php?id=${id}`),
};

// Activity Logs APIs
export const activityLogsAPI = {
  getAll: (params) => api.get('/activity-logs.php', { params }),
  getByModule: (module, params) => 
    api.get('/activity-logs.php', { params: { ...params, module } }),
  getByUser: (userId, params) => 
    api.get('/activity-logs.php', { params: { ...params, user_id: userId } }),
};

// Exam Schedule APIs
export const examScheduleAPI = {
  getAll: (params) => api.get('/exam-schedule.php', { params }),
  getByExam: (examId) => api.get('/exam-schedule.php', { params: { exam_id: examId } }),
  create: (data) => api.post('/exam-schedule.php', data),
  update: (id, data) => api.put(`/exam-schedule.php?id=${id}`, data),
  delete: (id) => api.delete(`/exam-schedule.php?id=${id}`),
};

export default api;

